import { api } from "@/modules/api";
import { useQuery } from "@tanstack/react-query";
import { FC, useState } from "react";
import { useNavigate } from "react-router";
import { Button, Flex, Table, Badge, TextField, Text, Code } from "@radix-ui/themes";
import { atlasRoutes } from "@/app/router/routes";
import { DateTime } from "luxon";
import styled from "@emotion/styled";
import { Search } from "lucide-react";

export const AssistantsRootPage: FC = () => {
  const navigate = useNavigate();
  const [spaceAccountId, setSpaceAccountId] = useState<string>("");
  const [searchInput, setSearchInput] = useState<string>("");

  // Search all assistant sessions (no filter) or by space account ID
  const assistantsResult = useQuery({
    queryKey: ["slack-assistant-sessions", spaceAccountId],
    queryFn: () =>
      api.post("/internal/v1/slack-assistant-sessions/search", {
        body: {
          ...(spaceAccountId ? { space_account_id: spaceAccountId } : {}),
          limit: 50,
        },
      }),
    staleTime: DateTime.fromObject({ minute: 1 }).toMillis(),
  });

  const handleSearch = () => {
    setSpaceAccountId(searchInput);
  };

  const handleClear = () => {
    setSearchInput("");
    setSpaceAccountId("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const assistants = assistantsResult.data?.data?.results ?? [];

  return (
    <Container>
      <br />
      <h1>Assistants</h1>
      <Text color="gray" size="2">
        View all assistant sessions or filter by Space Account ID.
      </Text>

      <br />
      <Flex gap="2" align="center">
        <TextField.Root
          placeholder="Filter by Space Account ID (optional)..."
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          onKeyDown={handleKeyDown}
          style={{ width: "400px" }}
        >
          <TextField.Slot>
            <Search size={16} />
          </TextField.Slot>
        </TextField.Root>
        <Button onClick={handleSearch}>Filter</Button>
        {spaceAccountId && (
          <Button variant="soft" onClick={handleClear}>
            Clear
          </Button>
        )}
      </Flex>

      <br />

      {assistantsResult.isLoading && <Text>Loading...</Text>}

      {assistantsResult.isError && (
        <Text color="red">Error: {assistantsResult.error?.message ?? "Unknown error"}</Text>
      )}

      {assistants.length === 0 && !assistantsResult.isLoading && (
        <Text color="gray">No assistant sessions found.</Text>
      )}

      {assistants.length > 0 && (
        <>
          <Text size="2" color="gray">
            Found {assistantsResult.data?.data?.total ?? assistants.length} assistant session(s).
          </Text>
          <br />
          <br />
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>User</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Slack User</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Threads</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Messages</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Last Updated</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {assistants.map(assistant => (
                <Table.Row key={assistant.id}>
                  <Table.Cell>
                    <Flex direction="column" gap="1">
                      <Text size="2" weight="medium">
                        {assistant.profile_display_name ?? "Unknown"}
                      </Text>
                      <Text size="1" color="gray">
                        {assistant.profile_email_address ??
                          assistant.space_account_id.substring(0, 8) + "..."}
                      </Text>
                    </Flex>
                  </Table.Cell>
                  <Table.Cell>
                    <Code size="1">{assistant.slack_user_id}</Code>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge variant="soft">{assistant.thread_count}</Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge variant="soft">{assistant.message_count}</Badge>
                  </Table.Cell>
                  <Table.Cell>{DateTime.fromISO(assistant.updated_at).toRelative()}</Table.Cell>
                  <Table.Cell>
                    <Button
                      size="1"
                      variant="soft"
                      onClick={() =>
                        navigate(
                          atlasRoutes.assistantsView({
                            params: { sessionId: assistant.session_id },
                          }).path
                        )
                      }
                    >
                      View
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </>
      )}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  padding: 20px;
`;
